import { v4 as uuid4 } from 'uuid'

type Modifier = {
  id?: string
  name: string
  kitchenName?: string
  quantity: number
  tabModifierId?: string
  parentModifierId?: string
}

type KitchenModifier = {
  id: string
  name: string
  catalogModifierId?: string
  quantity: number
  tabModifierId?: string
}

type Combo = {
  id?: string
  name?: string
  combine?: boolean
}

export type Product = {
  id: string
  name: string
  kitchenName?: string
  course?: string | null
  comments?: string
  quantity: number
  parentProduct?: string
  fromCombo?: boolean
  combo?: Combo
  categoryPosition?: number
  modifiers?: Modifier[]
  printerIds?: string[]
  copies?: number
  controlEnabled?: boolean
}

type KitchenProduct = {
  id: string
  name: string
  course: string | null
  comments?: string
  quantity: number
  tabProductId: string
  catalogProductId?: string
  fromCombo: boolean
  comboId?: string
  comboName?: string
  combine: boolean
  categoryPosition?: number
  modifiers?: KitchenModifier[]
}

type Printer = {
  id: string
  name: string
  type: string
  splitProducts?: boolean
}

export type Tab = {
  id: string
  virtualBrandId?: string
  allergyInfo?: string
  seats: any[]
  pickupType?: string
  source?: string
  code?: string
  kitchenNote?: string
  activationTime?: string
  schedulingTime?: string
}

type ControlPrinterConfig = {
  printerId?: string
  floorplanControlPrinter?: string
}

type DeliveryOrder = {
  estimatedDeliveryTime?: string
  courierName?: string
  courierPhoneNumber?: string
  customerName?: string
  customerPhoneNumber?: string
  pickupTime?: string
}

type ProductGroup = {
  printerId: string
  printerName: string
  copies?: number
  products: KitchenProduct[]
  printerType: string
}

type OrderVersion = {
  id: string
  sentTime: Date | number
  products: KitchenProduct[]
}

type Order = {
  id: string
  creationTime: Date
  versions: OrderVersion[]
  locationBrandId?: string
  tabId: string
  allergyInfo?: string
  printerId: string
  printerName: string
  copies?: number
  tabName: string
  estimatedDeliveryTime?: string
  seats: number
  pickupType?: string
  toGo: boolean
  source?: string
  code?: string
  courierName?: string
  courierPhoneNumber?: string
  customerName?: string
  customerPhoneNumber?: string
  pickupTime?: string
  waiterName: string
  note?: string
  stickerPart: string
  activationTime?: string
  virtualBrandName?: string
}

function regenerateIds(product: KitchenProduct): KitchenProduct {
  const kitchenProduct: KitchenProduct = {
    ...product,
    id: uuid4()
  }
  if (product.modifiers) {
    kitchenProduct.modifiers = product.modifiers.map(modifier => ({
      ...modifier,
      id: uuid4()
    }))
  }
  return kitchenProduct
}

function mapProduct(product: Product): KitchenProduct {
  const kitchenProduct: KitchenProduct = {
    name: product.kitchenName || product.name,
    course: product.course ?? null,
    comments: product.comments,
    quantity: product.quantity,
    tabProductId: product.id,
    catalogProductId: product.parentProduct,
    id: uuid4(),
    fromCombo: !!product.fromCombo,
    comboId: product.combo?.id,
    comboName: product.combo?.name,
    combine: product.combo?.combine || false,
    categoryPosition: product.categoryPosition
  }
  if (product.modifiers) {
    kitchenProduct.modifiers = product.modifiers.map(modifier => ({
      id: uuid4(),
      name: modifier.kitchenName || modifier.name,
      catalogModifierId: modifier.parentModifierId,
      quantity: modifier.quantity,
      tabModifierId: modifier.id
    }))
  }
  return kitchenProduct
}

/**
 * Determines if a control printer should be added to a product based on configuration and order type.
 * Control printers are used to print all dine-in orders to a central location for order control.
 *
 * @param product The product to check
 * @param tab The tab containing information about the order
 * @param controlPrinter The ID of the control printer to add
 * @returns True if the control printer should be added, false otherwise
 */
function shouldAddControlPrinter(product: Product, tab: Tab, controlPrinter?: string): boolean {
  if (!controlPrinter) return false
  if (product.controlEnabled !== true) return false

  const isDineIn = !tab.pickupType
  const isRestaurantSource = tab.source === 'Restaurant'

  return isDineIn || isRestaurantSource
}

/**
 * Generates a list of orders based on the provided products, tab, delivery order, and other parameters.
 * It groups products by printer and creates an order for each group.
 *
 * @param products List of products to be included in the orders
 * @param tab The tab containing information about the order
 * @param deliveryOrder Optional delivery order information
 * @param tabName Name of the tab
 * @param waiterName Name of the waiter
 * @param printers List of printers to be used for the orders
 * @param virtualBrandName Optional name of the virtual brand
 * @param controlPrinterConfig Optional control printer configuration
 * @returns An array of generated orders
 */
function generateOrders(
  products: Product[],
  tab: Tab,
  deliveryOrder: DeliveryOrder | null | undefined,
  tabName: string,
  waiterName: string,
  printers: Printer[],
  virtualBrandName?: string,
  controlPrinterConfig?: ControlPrinterConfig
): Order[] {
  const printersMap: Record<string, Printer> = printers.reduce(
    (acc, p) => ({ ...acc, [p.id]: p }),
    {} as Record<string, Printer>
  )

  const controlPrinter =
    controlPrinterConfig?.floorplanControlPrinter ?? controlPrinterConfig?.printerId
  const productsWithControlPrinter = products.map(product => {
    const printerIds = product.printerIds || []
    if (shouldAddControlPrinter(product, tab, controlPrinter)) {
      return {
        ...product,
        printerIds: Array.from(new Set([...printerIds, controlPrinter!]))
      }
    }

    return product
  })

  const groupedProductsMapped: Record<string, ProductGroup> = productsWithControlPrinter.reduce(
    (res: Record<string, ProductGroup>, product) => {
      const printerIds: string[] = product.printerIds || []
      printerIds.forEach(printerId => {
        const copies = product.copies
        const printer = printersMap[printerId]
        if (!printer) return
        if (printer.splitProducts) {
          for (let quantity = 1; quantity <= product.quantity; ++quantity) {
            res[`${product.id},${copies},${quantity}`] = {
              printerId: printerId,
              printerName: printer.name,
              copies: copies,
              products: [mapProduct({ ...product, quantity: 1 })],
              printerType: printer.type
            }
          }
        } else {
          const group = res[`${printerId},${copies}`] || {
            printerId: printerId,
            printerName: printer.name,
            copies: copies,
            products: [],
            printerType: printer.type
          }
          group.products.push(mapProduct(product))
          res[`${printerId},${copies}`] = group
        }
      })
      return res
    },
    {}
  )

  const groupedProducts: ProductGroup[] = Object.values(groupedProductsMapped)
  const totalStickers = groupedProducts.filter(product => product.printerType === 'ZPL').length
  let stickerPosition = 0
  const estimatedDeliveryTime = deliveryOrder?.estimatedDeliveryTime ?? tab.schedulingTime
  return groupedProducts.map(group => {
    const sentTime = new Date()
    if (group.printerType === 'ZPL') ++stickerPosition
    return {
      id: uuid4(),
      creationTime: sentTime,
      versions: [
        {
          id: uuid4(),
          sentTime,
          products: group.products
        }
      ],
      locationBrandId: tab.virtualBrandId,
      tabId: tab.id,
      allergyInfo: tab.allergyInfo,
      printerId: group.printerId,
      printerName: group.printerName,
      copies: group.copies,
      tabName,
      estimatedDeliveryTime,
      seats: tab.seats.length,
      pickupType: tab.pickupType,
      toGo: !!tab.pickupType && ['takeAway', 'delivery', 'ownDelivery'].includes(tab.pickupType),
      source: tab.source,
      code: tab.code,
      ...(deliveryOrder && {
        courierName: deliveryOrder.courierName,
        courierPhoneNumber: deliveryOrder.courierPhoneNumber,
        customerName: deliveryOrder.customerName,
        customerPhoneNumber: deliveryOrder.customerPhoneNumber,
        pickupTime: deliveryOrder.pickupTime
      }),
      waiterName,
      note: tab.kitchenNote,
      stickerPart: `${stickerPosition} / ${totalStickers}`,
      activationTime: tab.activationTime,
      virtualBrandName
    }
  })
}

/**
 * Generates an order version for a list of products.
 * It maps each product to a kitchen product and returns an order version with the current timestamp.
 *
 * @param products List of products to be included in the order version
 * @returns The generated order version
 */
function generateOrderVersion(products: Product[]): OrderVersion {
  const sentTime: number = Date.now()
  return {
    id: uuid4(),
    sentTime,
    products: products.map(product => mapProduct(product))
  }
}

/**
 * Generates an order version for a product that has been split.
 * It subtracts one from the quantity of the specified product and adds a new product.
 * This is used when a product is split into two or more products in the kitchen.
 *
 * @param currentVersionProducts Kitchen products in the current order version
 * @param splittedProductId The ID of the product that has been split
 * @param newProduct The new product that has been created from the split
 * @returns The new order version
 */
function generateOrderVersionForSplittedProduct(
  currentVersionProducts: KitchenProduct[],
  splittedProductId: string,
  newProduct: Product
): OrderVersion {
  const currentProductsWithoutSubtractedOne = currentVersionProducts
    .map(product => {
      if (product.tabProductId === splittedProductId) {
        return {
          ...product,
          quantity: product.quantity - 1
        }
      } else {
        return product
      }
    })
    .map(regenerateIds)

  const sentTime: number = Date.now()
  return {
    id: uuid4(),
    sentTime,
    products: [...currentProductsWithoutSubtractedOne, mapProduct(newProduct)]
  }
}

export default {
  generateOrders,
  generateOrderVersion,
  generateOrderVersionForSplittedProduct,
  shouldAddControlPrinter
}
